import posthog, { type PostHogConfig } from 'posthog-js'
import { PostHogProvider, useFeatureFlagEnabled } from 'posthog-js/react'
import { conf } from '../fe-inv-conf'
import type { ExternalUserAnalyticsProvider } from './make-user-analytics'
import { useCallback, useEffect, useState } from 'react'
import { _typedLocalStorage, getTelemetryUserData } from '../local-storage/local-storage'

type PostHogProviderChildren = React.ComponentProps<typeof PostHogProvider>['children']

export function makePostHogProvider(
  options: Partial<PostHogConfig>,
): ExternalUserAnalyticsProvider {
  const useFeatureFlagEnabledNonProd = (_: string) => true

  const NonProdProvider: ExternalUserAnalyticsProvider['Provider'] = ({ children }) => (
    <>{children as PostHogProviderChildren}</>
  )
  const ProdProvider: ExternalUserAnalyticsProvider['Provider'] = ({ children }) => (
    <PostHogProvider apiKey={conf.PUBLIC_POSTHOG_KEY} options={options}>
      <IdentifyUser />
      {children as PostHogProviderChildren}
    </PostHogProvider>
  )

  const enabled = conf.ENV === 'production'
  if (!['local', 'test'].includes(conf.ENV)) {
    console.info('PostHog enabled', enabled)
  }
  return {
    Provider: enabled ? ProdProvider : NonProdProvider,
    useFeatureFlagEnabled: enabled ? useFeatureFlagEnabled : useFeatureFlagEnabledNonProd,
  }
}

// biome-ignore lint/nursery/useComponentExportOnlyModules: <explanation>
function IdentifyUser() {
  const [done, setDone] = useState(false)

  const identifyUserIfLoggedIn = useCallback(() => {
    const token = getTelemetryUserData()

    if (token) {
      posthog.identify(token.entityUserId, {
        id: token.entityUserId,
        companyId: token.entityId,
      })
      console.info('PostHog identified user', token.entityUserId)
      setDone(true)
      return true
    }
  }, [])

  useEffect(() => {
    if (done) return

    // Identify user on mount if already logged in
    // eslint-disable-next-line react-hooks/set-state-in-effect
    const identified = identifyUserIfLoggedIn()
    if (identified) return

    // Listen for localStorage changes to identify users when they log in
    const cleanUp = _typedLocalStorage.onChange('invoicingAccessTokenPayload', () => {
      const identified = identifyUserIfLoggedIn()
      if (identified) cleanUp()
    })

    return () => {
      cleanUp()
    }
  }, [done, identifyUserIfLoggedIn])

  return null
}
